/**
 * The rules the ad token picker runs on (MPN-1932), with no DOM in sight.
 *
 * Everything here is a pure function of the field's text and where the caret is, so the
 * awkward cases -- a bracket typed mid-word, a `]` the writer already closed, an open
 * bracket that stopped being one -- can be tested without asking jsdom to lay anything out.
 *
 * This file knows nothing about whether a bracket string in the copy is valid. Finding and
 * warning about unknown tokens is MPN-1930's job, in both repos.
 */

/** One active token as `GET /api/v1/content/ad-tokens/` returns it. */
export type AdToken = {
    id: number;
    /**
     * The record the value is read from, or null when no record supplies it -- which is
     * every copywriter instruction (MPN-1952).
     */
    scope: string | null;
    source: string;
    /** The canonical spelling, brackets included: `[BRAND NAME]`. The only thing ever inserted. */
    token: string;
    help_text: string;
    record_field: string | null;
    /**
     * Whether anything will ever fill this bracket in on its own (MPN-1952).
     *
     * False for a copywriter instruction, whose bracket is a direction to a human --
     * `[CTA]`, `[NEED]`. It is registered, so validation does not call it unknown, and the
     * picker still inserts it; a person fills it in later, reading `help_text`.
     */
    is_automatable: boolean;
    /** Other spellings this token answers to. Search keys, never offered as choices. */
    aliases: string[];
};

/**
 * Whether the menu has to say this bracket will not fill itself in.
 *
 * Phrased against the contract rather than against `source`: `is_automatable` is the field
 * the server promises, and a second non-automatable source would be covered by it for free.
 */
export function isCopywriterInstruction(token: AdToken): boolean {
    return !token.is_automatable;
}

/**
 * A token the query matched, and which alias got it there.
 *
 * `alias` is null when the canonical itself matched. When it is set, the menu has to say so:
 * the writer typed one spelling and a different one is about to land in the copy.
 */
export type AdTokenMatch = {
    token: AdToken;
    alias: string | null;
};

/** An open bracket before the caret, and what has been typed since it. */
export type AdTokenContext = {
    /** Index of the `[` in the field's text. */
    start: number;
    /** The raw text between that `[` and the caret. */
    query: string;
};

/**
 * What the server counts as whitespace: Python's `str.isspace()`, which is also what `\s`
 * means in its `re`. Spelled out because JavaScript's `\s` is a different set -- it takes
 * U+FEFF and leaves out U+001C-U+001F and U+0085 -- and `normalize_ad_token` is the rule.
 */
const SERVER_WHITESPACE = "\\t\\n\\v\\f\\r\\x1c-\\x20\\x85\\xa0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000";
const PADDING = new RegExp(`^[${SERVER_WHITESPACE}]+|[${SERVER_WHITESPACE}]+$`, "g");
const WHITESPACE_RUN = new RegExp(`[${SERVER_WHITESPACE}]+`, "g");
const OPEN_PADDING = new RegExp(`\\[[${SERVER_WHITESPACE}]+`, "g");
const CLOSE_PADDING = new RegExp(`[${SERVER_WHITESPACE}]+\\]`, "g");

/**
 * The spelling rule, a line-for-line port of the server's `normalize_ad_token`:
 * case-insensitive, runs of whitespace collapsed to one space, padding trimmed -- around the
 * text and inside its brackets, so `[ Brand  Name ]` is `[brand name]`. Collapsed and not
 * removed, so `[BRANDNAME]` stays a different thing from `[BRAND NAME]` here too.
 *
 * Tested against `testdata/ad_token_spelling.json` (MPN-1930), derived from the server's
 * function. The typed text and the API's aliases both go through this one function, so a
 * lookup always compares like with like. The picker passes bracketless text through it,
 * where the bracket steps change nothing.
 */
export function normalizeAdTokenText(text: string): string {
    const collapsed = text.replace(PADDING, "").replace(WHITESPACE_RUN, " ").toLowerCase();
    return collapsed.replace(OPEN_PADDING, "[").replace(CLOSE_PADDING, "]");
}

/**
 * A token's spelling without its brackets, for display and for matching what the writer has
 * typed since the `[`. Aliases are stored as written and may or may not carry brackets, so
 * both go through here.
 */
export function adTokenLabel(spelling: string): string {
    let label = spelling.trim();
    if (label.startsWith("[")) label = label.slice(1);
    if (label.endsWith("]")) label = label.slice(0, -1);
    return label.trim();
}

/**
 * The bracket the caret is currently inside, or null if it is not inside one.
 *
 * Only a collapsed caret counts -- with a selection there is no single insertion point. The
 * nearest `[` before the caret opens the context, and anything between it and the caret that
 * could not be part of a token spelling (another bracket, a line break) means the writer has
 * moved on and there is no context any more.
 */
export function readAdTokenContext(
    value: string,
    selectionStart: number,
    selectionEnd: number,
): AdTokenContext | null {
    if (selectionStart !== selectionEnd) return null;
    const before = value.slice(0, selectionStart);
    const start = before.lastIndexOf("[");
    if (start === -1) return null;
    const query = before.slice(start + 1);
    if (/[[\]\r\n]/.test(query)) return null;
    return { start, query };
}

/**
 * Rank one token against the query, lower being better, or null for no match.
 *
 * Canonical beats alias, and a prefix beats a hit in the middle: someone typing "reg" wants
 * `[REG START]` above a token whose help text happens to contain the letters.
 */
function rankAdToken(token: AdToken, query: string): { rank: number; alias: string | null } | null {
    if (!query) return { rank: 0, alias: null };

    const canonical = normalizeAdTokenText(adTokenLabel(token.token));
    if (canonical.startsWith(query)) return { rank: 0, alias: null };
    if (canonical.includes(query)) return { rank: 1, alias: null };

    let contains: string | null = null;
    for (const alias of token.aliases) {
        const normalized = normalizeAdTokenText(adTokenLabel(alias));
        if (normalized.startsWith(query)) return { rank: 2, alias };
        if (contains === null && normalized.includes(query)) contains = alias;
    }
    return contains === null ? null : { rank: 3, alias: contains };
}

/** Every token the query matches, best first, then alphabetical so the order never jitters. */
export function matchAdTokens(tokens: AdToken[], query: string): AdTokenMatch[] {
    const normalized = normalizeAdTokenText(query);
    return tokens
        .map((token) => ({ token, ...rankAdToken(token, normalized) }))
        .filter((m): m is { token: AdToken; rank: number; alias: string | null } => typeof m.rank === "number")
        .sort((a, b) => a.rank - b.rank || a.token.token.localeCompare(b.token.token))
        .map(({ token, alias }) => ({ token, alias }));
}

/**
 * Replace the open bracket and whatever has been typed since it with the canonical token.
 *
 * A `]` sitting right at the caret is absorbed rather than left behind -- a writer who typed
 * `[]` and went back inside would otherwise end up with `[BRAND NAME]]`. The caret lands
 * after the closing bracket, ready for the next word.
 */
export function insertAdToken(
    value: string,
    caret: number,
    start: number,
    canonical: string,
): { value: string; caret: number } {
    const end = value.charAt(caret) === "]" ? caret + 1 : caret;
    return {
        value: value.slice(0, start) + canonical + value.slice(end),
        caret: start + canonical.length,
    };
}

/**
 * Whether the bracket was typed against the end of a word, where the token will land inside
 * it. The menu says so rather than refusing to open: suppressing it would silently do
 * nothing to a writer who meant to insert a token.
 */
export function isMidWordBracket(value: string, start: number): boolean {
    return start > 0 && /[A-Za-z0-9]/.test(value.charAt(start - 1));
}
